/**
 * Katie & Laura — I own this (Prestige PDP checkbox)
 *
 * Claim SOT: Customer metafield diary.owned via App Proxy
 *   GET  {proxy}/collection/owned
 *   POST {proxy}/collection/owned  { action: 'add'|'remove', productId, variantId, ... }
 * Same pattern as collection favorites. Soft-fail 404 until Worker routes land.
 *
 * Wishlist hearts / diary.wishlist are a different SOT — do not mix.
 * Never put WISHLIST_PROXY_SECRET in theme JS.
 *
 * Order-derived pieces stay checked and cannot be unchecked.
 * Claimed-only: uncheck POSTs remove.
 * Guest → account login return (pending claim resumes after sign-in).
 */
(function (global) {
  'use strict';

  var SELECTOR = '[data-kl-own-this]';
  var DEFAULT_PROXY = '/apps/kl-wishlist';
  var PENDING_KEY = 'kl_owned_pending';
  var painting = false;

  function config() {
    return global.KLOwnedConfig || {};
  }

  function strings() {
    var s = config().strings || {};
    return {
      label: s.label || 'I own this',
      saved: s.saved || 'Saved',
      savedLink: s.savedLink || 'My collection',
      removed: s.removed || 'Removed',
      error: s.error || 'Couldn’t save just now. Try again in a moment.',
      fromOrder: s.fromOrder || 'In your collection',
      removeConfirm: s.removeConfirm || 'Remove from My collection?'
    };
  }

  function normalizeId(id) {
    if (id == null || id === '') return '';
    var match = String(id).match(/(\d+)\s*$/);
    return match ? match[1] : String(id);
  }

  function normalizeItem(raw) {
    if (!raw || typeof raw !== 'object') return null;
    var productId = normalizeId(raw.productId || raw.product_id);
    if (!productId) return null;
    return {
      productId: productId,
      variantId: normalizeId(raw.variantId || raw.variant_id),
      handle: raw.handle ? String(raw.handle) : '',
      title: raw.title ? String(raw.title) : '',
      image: raw.image ? String(raw.image) : '',
      addedAt: raw.addedAt ? String(raw.addedAt) : new Date().toISOString()
    };
  }

  function normalizeList(value) {
    if (!value) return [];
    var list = value;
    if (typeof list === 'string') {
      try {
        list = JSON.parse(list);
      } catch (e) {
        return [];
      }
    }
    if (!Array.isArray(list)) return [];
    var seen = {};
    var out = [];
    for (var i = 0; i < list.length; i++) {
      var item = normalizeItem(list[i]);
      if (!item || seen[item.productId]) continue;
      seen[item.productId] = true;
      out.push(item);
    }
    return out;
  }

  function itemsFromResponse(data) {
    if (!data) return null;
    if (Array.isArray(data)) return normalizeList(data);
    if (Array.isArray(data.items)) return normalizeList(data.items);
    if (Array.isArray(data.owned)) return normalizeList(data.owned);
    return null;
  }

  function appProxyPath() {
    var raw = String(config().appProxyPath || '').trim();
    if (!raw) raw = DEFAULT_PROXY;
    if (raw.charAt(0) !== '/') raw = '/' + raw;
    return raw.replace(/\/+$/, '');
  }

  function collectionUrl() {
    var raw = String(config().collectionUrl || '/pages/my-collection').trim();
    if (!raw) raw = '/pages/my-collection';
    if (raw.charAt(0) !== '/') raw = '/' + raw;
    return raw;
  }

  function loginReturnUrl() {
    var login = String(config().loginUrl || '/account/login').trim() || '/account/login';
    var dest = '';
    try {
      dest = (location.pathname || '') + (location.search || '') + (location.hash || '');
    } catch (e) {
      dest = '/pages/my-collection';
    }
    var sep = login.indexOf('?') >= 0 ? '&' : '?';
    return login + sep + 'return_to=' + encodeURIComponent(dest) + '&checkout_url=' + encodeURIComponent(dest);
  }

  function proxyGet(route) {
    return fetch(appProxyPath() + route, {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (res) {
      if (!res.ok) {
        var err = new Error('kl-owned ' + res.status);
        err.status = res.status;
        throw err;
      }
      return res.json().catch(function () {
        return {};
      });
    });
  }

  function proxyPost(route, body) {
    return fetch(appProxyPath() + route, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      if (!res.ok) {
        var err = new Error('kl-owned ' + res.status);
        err.status = res.status;
        throw err;
      }
      return res.json().catch(function () {
        return {};
      });
    });
  }

  function currentVariantId(box) {
    var variantId = box && box.getAttribute('data-variant-id');
    var host = box && box.closest && box.closest('product-rerender, .product, product-info, .kl-own-this-slot');
    var form = host && host.querySelector && host.querySelector('form[action*="/cart/add"]');
    if (!form && typeof document !== 'undefined') {
      form = document.querySelector('form[action*="/cart/add"]');
    }
    var idInput = form && form.querySelector('[name="id"]:not([disabled])');
    if (idInput && idInput.value) variantId = idInput.value;
    return normalizeId(variantId);
  }

  function itemFromBox(box) {
    if (!box) return null;
    return normalizeItem({
      productId: box.getAttribute('data-product-id'),
      variantId: currentVariantId(box),
      handle: box.getAttribute('data-handle'),
      title: box.getAttribute('data-title'),
      image: box.getAttribute('data-image')
    });
  }

  function fromOrder(box) {
    return !!(box && box.getAttribute('data-from-order') === 'true');
  }

  function statusEl(box) {
    var wrap = box && box.closest && box.closest('[data-kl-own-this-wrap]');
    return wrap ? wrap.querySelector('[data-kl-own-this-status]') : null;
  }

  function showStatus(box, html, isError) {
    var el = statusEl(box);
    if (!el) return;
    el.hidden = !html;
    el.classList.toggle('is-error', !!isError);
    el.innerHTML = html || '';
  }

  function savedHtml() {
    var copy = strings();
    var href = collectionUrl();
    return (
      escapeHtml(copy.saved) +
      ' · <a href="' +
      escapeHtml(href) +
      '">' +
      escapeHtml(copy.savedLink) +
      '</a>'
    );
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function listHas(items, productId) {
    var id = normalizeId(productId);
    if (!id || !items) return false;
    for (var i = 0; i < items.length; i++) {
      if (items[i].productId === id) return true;
    }
    return false;
  }

  function paintBox(box, claimed) {
    if (!box) return;
    painting = true;
    if (fromOrder(box)) {
      box.checked = true;
      box.disabled = true;
      showStatus(box, escapeHtml(strings().fromOrder) + ' · <a href="' + escapeHtml(collectionUrl()) + '">' + escapeHtml(strings().savedLink) + '</a>', false);
    } else {
      box.disabled = false;
      box.checked = !!claimed;
      if (claimed) showStatus(box, savedHtml(), false);
      else showStatus(box, '', false);
    }
    painting = false;
  }

  function paintAll(items) {
    var boxes = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < boxes.length; i++) {
      var box = boxes[i];
      var id = normalizeId(box.getAttribute('data-product-id'));
      paintBox(box, listHas(items, id) || fromOrder(box));
    }
  }

  function readPending() {
    try {
      return normalizeItem(JSON.parse(global.sessionStorage.getItem(PENDING_KEY) || 'null'));
    } catch (e) {
      return null;
    }
  }

  function writePending(item) {
    try {
      if (!item) global.sessionStorage.removeItem(PENDING_KEY);
      else global.sessionStorage.setItem(PENDING_KEY, JSON.stringify(item));
    } catch (e) {}
  }

  function claim(box, added) {
    var item = itemFromBox(box);
    if (!item) return;
    var copy = strings();
    var body = Object.assign({ action: added ? 'add' : 'remove' }, item);
    if (added) body.addedAt = item.addedAt || new Date().toISOString();
    if (added) showStatus(box, savedHtml(), false);
    else showStatus(box, escapeHtml(copy.removed), false);
    proxyPost('/collection/owned', body).catch(function () {
      /* 404 until Worker ships — optimistic UI stays */
    });
  }

  function onBoxClick(event) {
    var box = event.target && event.target.closest && event.target.closest(SELECTOR);
    if (!box) return;
    if (fromOrder(box) || box.disabled) {
      event.preventDefault();
      paintBox(box, true);
      return;
    }
    if (!config().loggedIn) {
      event.preventDefault();
      writePending(itemFromBox(box));
      global.location.href = loginReturnUrl();
    }
  }

  function onBoxChange(event) {
    var box = event.target && event.target.closest && event.target.closest(SELECTOR);
    if (!box || painting) return;
    if (fromOrder(box) || box.disabled) {
      paintBox(box, true);
      return;
    }
    if (!config().loggedIn) {
      painting = true;
      box.checked = false;
      painting = false;
      writePending(itemFromBox(box));
      global.location.href = loginReturnUrl();
      return;
    }
    if (!box.checked) {
      painting = true;
      box.checked = true;
      painting = false;
      askRemoveConfirm(function (ok) {
        if (!ok) return;
        painting = true;
        box.checked = false;
        painting = false;
        claim(box, false);
      });
      return;
    }
    claim(box, true);
  }

  function askRemoveConfirm(cb) {
    var message = strings().removeConfirm || 'Remove from My collection?';
    var ask = global.klDiaryConfirm;
    if (typeof ask === 'function') {
      ask(message, cb);
      return;
    }
    cb(typeof global.confirm === 'function' ? !!global.confirm(message) : false);
  }

  function hydrate(items) {
    var seeded = normalizeList(items);
    paintAll(seeded);
    if (!config().loggedIn || !appProxyPath()) return Promise.resolve(seeded);
    return proxyGet('/collection/owned')
      .then(function (data) {
        var next = itemsFromResponse(data);
        if (next) {
          paintAll(next);
          return next;
        }
        return seeded;
      })
      .catch(function () {
        return seeded;
      });
  }

  function resumePending(items) {
    if (!config().loggedIn) return;
    var pending = readPending();
    if (!pending) return;
    var box = document.querySelector(SELECTOR + '[data-product-id="' + pending.productId + '"]');
    if (!box) return;
    writePending(null);
    if (fromOrder(box) || listHas(items, pending.productId)) {
      paintBox(box, true);
      return;
    }
    painting = true;
    box.checked = true;
    painting = false;
    claim(box, true);
  }

  function init() {
    var boxes = document.querySelectorAll(SELECTOR);
    if (!boxes.length) return;
    hydrate(config().customerOwned).then(function (items) {
      resumePending(items || []);
    });
  }

  document.addEventListener('click', onBoxClick, true);
  document.addEventListener('change', onBoxChange, true);

  if (typeof MutationObserver !== 'undefined') {
    var timer = null;
    var observer = new MutationObserver(function (mutations) {
      var needs = false;
      for (var i = 0; i < mutations.length; i++) {
        var nodes = mutations[i].addedNodes;
        for (var j = 0; j < nodes.length; j++) {
          var node = nodes[j];
          if (node.nodeType !== 1) continue;
          if ((node.matches && node.matches(SELECTOR)) || (node.querySelector && node.querySelector(SELECTOR))) {
            needs = true;
            break;
          }
        }
        if (needs) break;
      }
      if (!needs) return;
      clearTimeout(timer);
      timer = setTimeout(function () {
        hydrate(config().customerOwned);
      }, 50);
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  global.KLOwned = {
    metafield: { namespace: 'diary', key: 'owned' },
    hydrate: hydrate
  };
})(typeof window !== 'undefined' ? window : this);
